// src/utils/api-electrical-calibration.js
//
// Capa de datos del módulo de calibración ELÉCTRICA (multímetro/amperímetro/
// ohmímetro). Espeja el patrón del piloto de presión pero sobre las tablas
// genéricas (calibration_sessions / _blocks / _points / _results) y con las
// 8 funciones eléctricas.
//
// Cálculo de F2 (primera pasada): error = instrumento − patrón; incertidumbre
// = RSS( U_patrón(clase 0,05% rectangular) , U_resolución ), k=2. El presupuesto
// completo (resolución por función, deriva, etc.) se refina en F3.

import { supabase } from './supabase';
import {
  ELECTRICAL_FUNCTIONS,
  buildDefaultElectricalBlocks,
  parseElectricalValue,
  standardStdUncertainty,
  resolutionStdUncertainty,
} from './electrical-calibration-profile';
// Reutilizamos el log ambiental diario (es genérico del laboratorio).
export {
  getTodayDailyEnvironmentLog,
  upsertTodayDailyEnvironmentLog,
} from './api-pressure-manometer-pilot';

export const DEFAULT_VALIDITY_MONTHS = 12; // eléctrica: 1 año típico
const EXCEL_VALIDITY_DAYS_BY_MONTHS = { 3: 90, 6: 181, 12: 365, 24: 730 };

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function toNullableNumber(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function sanitizePatch(patch = {}) {
  const out = {};
  Object.entries(patch || {}).forEach(([k, v]) => {
    if (v === undefined) return;
    if (typeof v === 'number' && !Number.isFinite(v)) return;
    out[k] = v;
  });
  return out;
}
async function currentUserId() {
  return (await supabase.auth.getUser())?.data?.user?.id ?? null;
}

export function resolveValidityDays(months = DEFAULT_VALIDITY_MONTHS) {
  const m = Number(months);
  if (!Number.isFinite(m) || m <= 0) return EXCEL_VALIDITY_DAYS_BY_MONTHS[DEFAULT_VALIDITY_MONTHS];
  return EXCEL_VALIDITY_DAYS_BY_MONTHS[m] ?? Math.round((365 / 12) * m);
}
export function addValidityDaysToIsoDate(isoDate, months) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + resolveValidityDays(months));
  return d.toISOString().slice(0, 10);
}

// ── Cálculo de un punto (compartido: live en la UI y persistido) ────────────
// Presupuesto de incertidumbre por comparación (3 componentes, RSS, k=2):
//   u_standard   = U del calibrador  = (clase% · |valor|)/√3
//   u_drift      = deriva calibrador = (deriva% · |valor|)/√3
//   u_resolution = resolución DUT    = (resolución/2)/√3   ← POR FUNCIÓN
export function computeElectricalPointMetrics({
  patternValue,
  instrumentValue,
  resolution = 0,
  accuracyClass = 0.05,
  driftClass = 0,
  classPct = null,
}) {
  if (!Number.isFinite(patternValue) || !Number.isFinite(instrumentValue)) return null;
  const errorAbs = instrumentValue - patternValue; // instrumento − patrón
  const errorPct = patternValue !== 0 ? (errorAbs / patternValue) * 100 : null;
  const uStandard = standardStdUncertainty(patternValue, accuracyClass);
  const uDrift = standardStdUncertainty(patternValue, driftClass);
  const uResolution = resolutionStdUncertainty(resolution);
  const uc = Math.sqrt(uStandard ** 2 + uDrift ** 2 + uResolution ** 2);
  const uExpanded = uc * 2;
  const uReported = Math.max(uExpanded, Math.abs(toNum(resolution, 0)));
  let inTolerance = null;
  if (classPct != null && Number.isFinite(classPct) && classPct > 0 && errorPct != null) {
    inTolerance = Math.abs(errorPct) <= classPct;
  }
  return {
    pattern_mean: patternValue,
    instrument_mean: instrumentValue,
    error_abs: errorAbs,
    error_pct: errorPct,
    u_standard: uStandard,
    u_drift: uDrift,
    u_resolution: uResolution,
    uc,
    k: 2,
    u_expanded: uExpanded,
    u_reported: uReported,
    in_tolerance: inTolerance,
  };
}

// ── Patrón eléctrico por defecto (GFUVE) ────────────────────────────────────
async function getDefaultElectricalStandard() {
  const { data, error } = await supabase
    .from('calibration_reference_standards')
    .select('*')
    .eq('family', 'electrical')
    .eq('active', true)
    .order('code', { ascending: true })
    .limit(1);
  if (error) throw error;
  return (data && data[0]) || null;
}

async function getTodayDailyEnv() {
  const { getTodayDailyEnvironmentLog } = await import('./api-pressure-manometer-pilot');
  try { return await getTodayDailyEnvironmentLog(); } catch { return null; }
}

// ── Sesiones ────────────────────────────────────────────────────────────────
export async function createElectricalSession({
  instrumentId,
  serviceOrderId = null,
  serviceOrderCode = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_VALIDITY_MONTHS,
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');

  const { data: instrument, error: iErr } = await supabase
    .from('instruments')
    .select(`id, tag, lab_id, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
             client:client_id ( id, client_number, name, address, locality, province )`)
    .eq('id', instrumentId)
    .single();
  if (iErr) throw iErr;

  const [standard, dailyEnv] = await Promise.all([getDefaultElectricalStandard(), getTodayDailyEnv()]);

  const payload = {
    family: 'electrical',
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    service_order_code: serviceOrderCode,
    certificate_type: certificateType,
    status: 'draft',
    template_code: 'R-DT-PC-II-RR',
    template_revision: '02',

    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,

    instrument_object: instrument?.type || 'Multímetro',
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_unit: instrument?.unit || null,
    instrument_resolution: instrument?.resolution ?? null,
    instrument_range_min: instrument?.range_min ?? null,
    instrument_range_max: instrument?.range_max ?? null,
    instrument_class: toNullableNumber(instrument?.tolerance),

    standard_id: standard?.id ?? null,
    standard_name: standard ? `${standard.brand || ''} ${standard.model || ''}`.trim() || standard.name : null,
    standard_serial: standard?.serial ?? null,
    standard_certificate: standard?.certificate_no ?? null,
    standard_calibration_date: standard?.calibration_date ?? null,
    standard_accuracy_class: standard?.accuracy_class ?? 0.05,
    standard_drift_class: standard?.drift_class ?? 0,

    ambient_pressure_avg: dailyEnv?.pressure_hpa ?? null,
    ambient_temp_avg: dailyEnv?.temp_c ?? null,
    ambient_humidity_avg: dailyEnv?.humidity_rh ?? null,

    validity_months: Number(validityMonths) || DEFAULT_VALIDITY_MONTHS,
    created_by: await currentUserId(),
  };

  const { data: session, error } = await supabase
    .from('calibration_sessions')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;

  // Crear los 8 bloques (funciones) de una.
  const blocks = buildDefaultElectricalBlocks().map((b) => ({ ...b, session_id: session.id }));
  const { error: bErr } = await supabase.from('calibration_session_blocks').insert(blocks);
  if (bErr) throw bErr;

  return session;
}

export async function getElectricalSession(sessionId) {
  const { data, error } = await supabase.from('calibration_sessions').select('*').eq('id', sessionId).single();
  if (error) throw error;
  return data;
}

export async function updateElectricalSession(sessionId, patch) {
  const { data, error } = await supabase
    .from('calibration_sessions')
    .update({ ...sanitizePatch(patch), updated_at: new Date().toISOString(), updated_by: await currentUserId() })
    .eq('id', sessionId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listElectricalSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase
    .from('calibration_sessions')
    .select('*')
    .eq('instrument_id', instrumentId)
    .eq('family', 'electrical')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ── Bloques (funciones) ──────────────────────────────────────────────────────
export async function listElectricalBlocks(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_blocks')
    .select('*')
    .eq('session_id', sessionId)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function updateBlock(blockId, patch) {
  const { data, error } = await supabase
    .from('calibration_session_blocks')
    .update(sanitizePatch(patch))
    .eq('id', blockId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function setBlockApplicable(blockId, applicable) {
  return updateBlock(blockId, { applicable: !!applicable });
}

// ── Puntos ────────────────────────────────────────────────────────────────
// rows: [{ point_no, setpoint, pattern, instrument }] con valores en TEXTO
// (se parsean; soporta "1K", "5M", "10,01M"). unit del bloque.
export async function saveElectricalBlockPoints(sessionId, block, rows) {
  if (!sessionId || !block?.id) throw new Error('sesión y bloque requeridos');
  const unit = block.unit || '';
  const payload = [];
  (rows || []).forEach((r) => {
    const pointNo = Number(r.point_no);
    if (!Number.isFinite(pointNo)) return;
    const sp = parseElectricalValue(r.setpoint);
    const pat = parseElectricalValue(r.pattern);
    const ins = parseElectricalValue(r.instrument);
    // guardamos solo si hay patrón e instrumento numéricos válidos
    if (!Number.isFinite(pat.value) || !Number.isFinite(ins.value)) return;
    payload.push({
      session_id: sessionId,
      block_id: block.id,
      point_no: pointNo,
      series_no: 1,
      direction: 'NA',
      setpoint: Number.isFinite(sp.value) ? sp.value : (Number.isFinite(pat.value) ? pat.value : null),
      pattern_reading: pat.value,
      instrument_reading: ins.value,
      pattern_reading_raw: pat.raw || null,
      instrument_reading_raw: ins.raw || null,
      unit,
    });
  });
  if (!payload.length) return [];
  const { data, error } = await supabase
    .from('calibration_session_points')
    .upsert(payload, { onConflict: 'session_id,block_id,point_no,series_no,direction' })
    .select('*');
  if (error) throw error;
  return data || [];
}

export async function listElectricalPoints(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_points')
    .select('*')
    .eq('session_id', sessionId)
    .order('block_id', { ascending: true })
    .order('point_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ── Cálculo de resultados ────────────────────────────────────────────────────
export async function calculateElectricalResults(sessionId) {
  const [session, blocks, points] = await Promise.all([
    getElectricalSession(sessionId),
    listElectricalBlocks(sessionId),
    listElectricalPoints(sessionId),
  ]);

  const accuracyClass = toNum(session.standard_accuracy_class, 0.05);
  const driftClass = toNum(session.standard_drift_class, 0);
  const classPct = toNullableNumber(session.instrument_class);
  const sessionRes = toNum(session.instrument_resolution, 0);

  // Resolución POR FUNCIÓN (cae a la resolución de sesión si el bloque no tiene).
  const resByBlock = {};
  const applicableBlockIds = new Set();
  for (const b of blocks) {
    if (b.applicable) applicableBlockIds.add(b.id);
    const r = toNullableNumber(b.resolution);
    resByBlock[b.id] = r != null ? r : sessionRes;
  }

  const out = [];
  for (const p of points) {
    if (!applicableBlockIds.has(p.block_id)) continue;
    const metrics = computeElectricalPointMetrics({
      patternValue: toNum(p.pattern_reading, NaN),
      instrumentValue: toNum(p.instrument_reading, NaN),
      resolution: resByBlock[p.block_id] ?? 0,
      accuracyClass,
      driftClass,
      classPct,
    });
    if (!metrics) continue;
    out.push({ session_id: sessionId, block_id: p.block_id, point_no: p.point_no, ...metrics });
  }

  // limpiar resultados anteriores de la sesión y reinsertar
  await supabase.from('calibration_session_results').delete().eq('session_id', sessionId);
  if (out.length) {
    const { error } = await supabase.from('calibration_session_results').insert(out);
    if (error) throw error;
  }
  await updateElectricalSession(sessionId, { status: 'calculated' });
  return out;
}

export async function listElectricalResults(sessionId) {
  const { data, error } = await supabase
    .from('calibration_session_results')
    .select('*')
    .eq('session_id', sessionId)
    .order('point_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function closeElectricalSession(sessionId, { certificateNumber = null, certificateUrl = null } = {}) {
  const patch = { status: 'closed', certificate_created_at: new Date().toISOString() };
  if (certificateNumber) patch.certificate_number = certificateNumber;
  if (certificateUrl) patch.certificate_url = certificateUrl;
  return updateElectricalSession(sessionId, patch);
}
